import React from 'react';
import {
  Spinner,
  UserCircle,
  CheckCircle,
  File,
  ArrowSquareOut,
} from '@phosphor-icons/react';
import {
  DueDiligenceResponseHistory,
  ResponseChangeType,
  statusColorToClasses,
  responseChangeTypeLabels,
} from 'shared/types/due-diligence';

interface DueDiligenceHistoryPanelProps {
  history: DueDiligenceResponseHistory[];
  isLoading: boolean;
  getUserName: (userId: string | undefined) => string | null;
}

/**
 * Component to display audit trail history for a question.
 * Extracted from DueDiligenceResponseModal for reuse in the detail page.
 */
const DueDiligenceHistoryPanel: React.FC<DueDiligenceHistoryPanelProps> = ({
  history,
  isLoading,
  getUserName,
}) => {
  // Determine if an event is system (AI) or user triggered
  const isSystemEvent = (changeType: string) => {
    return changeType === 'ai_extracted' || changeType === 'ai_validated' || changeType === 'ai_edited';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Spinner size={20} className="animate-spin text-neutral-secondary-mid" />
        <span className="ml-2 text-sm text-neutral-secondary-mid">Geschiedenis laden...</span>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <p className="text-xs text-neutral-secondary-mid py-2">Geen geschiedenis beschikbaar</p>
    );
  }

  return (
    <div className="space-y-2 max-h-80 overflow-y-auto">
      {history.map((record, index) => {
        const isSystem = isSystemEvent(record.changeType);
        return (
          <div
            key={record.id}
            className={`p-2.5 rounded text-xs border-l-3 ${
              index === 0
                ? 'bg-blue-50 border border-blue-100 border-l-blue-500'
                : isSystem
                  ? 'bg-violet-50/50 border border-violet-100/50 border-l-violet-400'
                  : 'bg-neutral-50 border border-neutral-100 border-l-moss-400'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {/* System vs User indicator */}
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                  isSystem ? 'bg-violet-100 text-violet-700' : 'bg-moss-100 text-moss-700'
                }`}>
                  {isSystem ? 'Systeem' : 'Gebruiker'}
                </span>
                {/* Change type badge */}
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                  record.changeType === 'ai_extracted' ? 'bg-blue-100 text-blue-700' :
                  record.changeType === 'ai_validated' ? 'bg-green-100 text-green-700' :
                  record.changeType === 'ai_edited' ? 'bg-orange-100 text-orange-700' :
                  record.changeType === 'validated' ? 'bg-green-100 text-green-700' :
                  record.changeType === 'created' ? 'bg-purple-100 text-purple-700' :
                  'bg-neutral-100 text-neutral-700'
                }`}>
                  {responseChangeTypeLabels[record.changeType as ResponseChangeType] || record.changeType}
                </span>
                {/* Current indicator for first record */}
                {index === 0 && (
                  <span className="text-[10px] text-blue-600 font-medium">Huidig</span>
                )}
              </div>
              <span className="text-neutral-secondary-mid">
                {new Date(record.createdAt).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })}
              </span>
            </div>
            <div className="mt-1.5 space-y-1">
              {/* Who made the change - with icon based on user vs system */}
              {record.changedBy && (
                <p className="flex items-center gap-1">
                  <UserCircle size={12} className={isSystem ? 'text-violet-400' : 'text-moss-500'} />
                  <span className="text-neutral-secondary-dark">
                    {isSystem ? 'Verwerkt door: ' : 'Gewijzigd door: '}
                    <span className="font-medium">{getUserName(record.changedBy) || record.changedBy}</span>
                  </span>
                </p>
              )}
              {/* Change description */}
              {record.changeDescription && (
                <p className="text-neutral-secondary-dark italic">{record.changeDescription}</p>
              )}
              {/* Answer value */}
              {record.selectedOption && (
                <p className="flex items-center gap-1">
                  <span className={`w-2 h-2 rounded-full ${statusColorToClasses[record.selectedOption.statusColor]}`} />
                  <span>Antwoord: <span className="font-medium">{record.selectedOption.label}</span></span>
                </p>
              )}
              {/* Extracted values */}
              {record.freeTextAnswer && (
                <p>Tekst: <span className="font-mono">{record.freeTextAnswer}</span></p>
              )}
              {record.numberAnswer !== undefined && record.numberAnswer !== null && (
                <p>Getal: <span className="font-mono">{record.numberAnswer}</span></p>
              )}
              {record.dateAnswer && (
                <p>Datum: <span className="font-mono">{new Date(record.dateAnswer).toLocaleDateString('nl-NL')}</span></p>
              )}
              {record.booleanAnswer !== undefined && record.booleanAnswer !== null && (
                <p>Waarde: <span className="font-mono">{record.booleanAnswer ? 'Ja' : 'Nee'}</span></p>
              )}
              {/* Source file with dates */}
              {record.sourceFileLog && (
                <div className="mt-1 p-1.5 bg-blue-50/50 rounded border border-blue-100/50">
                  <p className="flex items-center gap-1">
                    <File size={12} className="text-blue-500" />
                    <span>Bron: </span>
                    {record.sourceFileLog.sharePointFileUrl ? (
                      <a
                        href={record.sourceFileLog.sharePointFileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline inline-flex items-center gap-0.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {record.sourceFileLog.fileName || record.sourceFileLog.label || `Document #${record.sourceFileLog.id}`}
                        <ArrowSquareOut size={10} />
                      </a>
                    ) : (
                      <span>{record.sourceFileLog.fileName || record.sourceFileLog.label || `Document #${record.sourceFileLog.id}`}</span>
                    )}
                  </p>
                  <div className="flex gap-3 mt-1 text-[10px]">
                    {record.sourceFileLog.documentDate && (
                      <span>Doc: {new Date(record.sourceFileLog.documentDate).toLocaleDateString('nl-NL')}</span>
                    )}
                    {record.sourceFileLog.expirationDate && (
                      <span className={new Date(record.sourceFileLog.expirationDate) < new Date() ? 'text-red-600' : ''}>
                        Verval: {new Date(record.sourceFileLog.expirationDate).toLocaleDateString('nl-NL')}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {/* AI confidence */}
              {record.aiConfidence !== undefined && record.aiConfidence !== null && (
                <p>AI zekerheid: {Math.round(record.aiConfidence * 100)}%</p>
              )}
              {/* Validation info */}
              {record.isValidated && record.validatedBy && (
                <p className="flex items-center gap-1 text-green-600">
                  <CheckCircle weight="fill" size={12} />
                  <span>Gevalideerd door {getUserName(record.validatedBy) || record.validatedBy}</span>
                  {record.validatedAt && (
                    <span className="text-neutral-secondary-mid ml-1">
                      op {new Date(record.validatedAt).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })}
                    </span>
                  )}
                </p>
              )}
              {/* Notes */}
              {record.notes && (
                <p className="italic text-neutral-secondary-dark">"{record.notes}"</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DueDiligenceHistoryPanel;
